import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, Button, Typography, useMediaQuery, Snackbar, Alert } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

// Theme and styling
import { createAppTheme } from './theme/theme';

// Layout components
import Navigation from './components/layout/Navigation';
import Sidebar from './components/layout/Sidebar';
import LoadingScreen from './components/common/LoadingScreen';
import ErrorBoundary from './components/common/ErrorBoundary';
// FloatingChatWidget import removed — will re-add when ready
import FloatingGlossary from './components/FloatingGlossary';
import RouteTransitionLoader from './components/common/RouteTransitionLoader';

// Page components
import Dashboard from './pages/Dashboard';
import Players from './pages/Players';
import PlayerDetail from './pages/PlayerDetail';
import Teams from './pages/Teams';
import TeamDetail from './pages/TeamDetail';
import Standings from './pages/Standings';
// Leaders merged into Explorer
import Trades from './pages/Trades';
import Settings from './pages/Settings';
import BoxscorePage from './pages/BoxscorePage';
import Scores from './pages/Scores';
import Explorer from './pages/Explorer';
import PitchLab from './pages/PitchLab';
import AtBatExplorerPage from './pages/AtBatExplorerPage';
import Glossary from './pages/Glossary';
// CompareTeams still merged into Explorer
// LiveGames merged into Scores page
import HotColdStreaks from './pages/HotColdStreaks';
import FantasyDFS from './pages/FantasyDFS';
import CvrMovers from './pages/CvrMovers';
import MomentumBoard from './pages/MomentumBoard';
import Admin from './pages/Admin';
// SalaryExplorer merged into Explorer
import AwardsTracker from './pages/AwardsTracker';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import RequirePro from './components/common/RequirePro';

// Hooks and utilities
import { healthCheck } from './services/apiService';
import { YearProvider } from './context/YearContext';
import { SettingsProvider } from './context/SettingsContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { AuthProvider, useAuth } from './context/AuthContext';

// Check for reduced motion preference
const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

// Pre-warm API responses for Pro pages so data is ready when the user navigates
function ProPagePrefetcher() {
  const { isPaid } = useAuth();
  React.useEffect(() => {
    if (!isPaid) return;
    const year = new Date().getFullYear();
    const urls = [
      `/api/v2/stats/leaders?category=batting&stat=ops&limit=200&minGames=5&minAtBats=15&mode=rolling&year=${year}`,
      `/api/v2/stats/leaders?category=pitching&stat=era&limit=100&minGames=3&minInnings=5&mode=rolling&year=${year}`,
      `/api/v2/trade/leaders?limit=500&minGames=5&category=all&mode=rolling&year=${year}`,
    ];
    // Fire-and-forget — just warms the nginx + browser HTTP cache
    urls.forEach(url => fetch(url).catch(() => {}));
  }, [isPaid]);
  return null;
}

// Slim banner shown to signed-in users who haven't verified their email yet
function VerifyEmailBanner() {
  const { user, resendVerification } = useAuth();
  const [status, setStatus] = React.useState(null); // 'sent' | 'error' | 'sending'
  const [dismissed, setDismissed] = React.useState(false);

  if (!user || user.emailVerified !== false || dismissed) return null;

  const resend = async () => {
    setStatus('sending');
    try { await resendVerification(); setStatus('sent'); }
    catch { setStatus('error'); }
  };

  return (
    <Alert
      severity="warning"
      onClose={() => setDismissed(true)}
      sx={{ borderRadius: 0, justifyContent: 'center', py: 0.25 }}
      action={
        <Button color="inherit" size="small" disabled={status === 'sending' || status === 'sent'} onClick={resend} sx={{ fontWeight: 700, textTransform: 'none' }}>
          {status === 'sent' ? 'Sent!' : status === 'sending' ? 'Sending…' : 'Resend email'}
        </Button>
      }
    >
      Please verify your email — check your inbox for a link from The Cycle.
    </Alert>
  );
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Animated route wrapper — uses useLocation (must be inside <Router>)
function AnimatedRoutes() {
  const location = useLocation();
  const [subscribedSnack, setSubscribedSnack] = React.useState(false);
  const [canceledSnack, setCanceledSnack] = React.useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('subscribed') === '1') {
      setSubscribedSnack(true);
      params.delete('subscribed');
      window.history.replaceState({}, '', location.pathname + (params.toString() ? '?' + params.toString() : ''));
    }
    if (params.get('canceled') === '1') {
      setCanceledSnack(true);
      params.delete('canceled');
      window.history.replaceState({}, '', location.pathname + (params.toString() ? '?' + params.toString() : ''));
    }
  }, [location.search, location.pathname]);

  return (
    <>
      <ScrollToTop />
      <ProPagePrefetcher />
      <Snackbar
        open={subscribedSnack}
        autoHideDuration={6000}
        onClose={() => setSubscribedSnack(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSubscribedSnack(false)} sx={{ fontWeight: 600 }}>
          Welcome to The Cycle Pro! 🎉 You now have full access.
        </Alert>
      </Snackbar>
      <Snackbar
        open={canceledSnack}
        autoHideDuration={5000}
        onClose={() => setCanceledSnack(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" onClose={() => setCanceledSnack(false)} sx={{ fontWeight: 600 }}>
          Checkout canceled — no charge was made.
        </Alert>
      </Snackbar>

      <AnimatePresence mode="sync">
        <motion.div
          key={location.pathname}
          initial={prefersReducedMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={false}
          transition={{ duration: 0.1, ease: 'easeOut' }}
          style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        >
        <Routes>
          {/* Dashboard */}
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />

          {/* Players */}
          <Route path="/players" element={<Players />} />
          <Route path="/players/batting" element={<Players category="batting" />} />
          <Route path="/players/pitching" element={<Players category="pitching" />} />
          <Route path="/players/profile/:slug" element={<PlayerDetail />} />
          <Route path="/players/:team/:playerName/:year" element={<PlayerDetail />} />
          <Route path="/players/:playerId" element={<PlayerDetail />} />

          {/* Teams */}
          <Route path="/teams" element={<Teams />} />
          <Route path="/teams/batting" element={<Teams category="batting" />} />
          <Route path="/teams/pitching" element={<Teams category="pitching" />} />
          <Route path="/teams/:teamId" element={<TeamDetail />} />
          <Route path="/teams/:teamId/:year" element={<TeamDetail />} />

          {/* Standings */}
          <Route path="/standings" element={<Standings />} />

          {/* Scores */}
          <Route path="/scores" element={<Scores />} />
          <Route path="/scores/:gamePk" element={<Scores />} />

          {/* Leaders → Players (Leaders tab removed, /players has the leaderboard) */}
          <Route path="/leaders" element={<Navigate to="/players" replace />} />

          {/* Explorer — free */}
          <Route path="/explorer" element={<Explorer />} />
          <Route path="/pitch-lab" element={<PitchLab />} />
          <Route path="/at-bat-explorer" element={<AtBatExplorerPage />} />

          {/* Glossary */}
          <Route path="/glossary" element={<Glossary />} />

          {/* Compare pages retired — Compare Teams still in Explorer */}
          <Route path="/compare-players" element={<Navigate to="/players" replace />} />
          <Route path="/compare-teams" element={<Navigate to="/explorer?tab=compare-teams" replace />} />

          {/* Live Games — merged into Scores */}
          <Route path="/live" element={<Navigate to="/scores" replace />} />

          {/* Spray Chart page retired — lives on as an Explorer tab */}
          <Route path="/spray-chart" element={<Navigate to="/explorer?tab=spray-charts" replace />} />

          {/* Momentum Board — combines CVR Movers + Hot/Cold Streaks */}
          <Route path="/momentum" element={<RequirePro featureName="Momentum Board"><MomentumBoard /></RequirePro>} />
          {/* Legacy redirects */}
          <Route path="/cvr-movers" element={<Navigate to="/momentum" replace />} />
          <Route path="/streaks"    element={<Navigate to="/momentum" replace />} />

          {/* Fantasy / DFS — Pro only */}
          <Route path="/fantasy" element={<RequirePro featureName="Fantasy / DFS Optimizer"><FantasyDFS /></RequirePro>} />

          {/* Salary → Explorer */}
          <Route path="/salaries" element={<Navigate to="/explorer" replace />} />

          {/* Awards Tracker */}
          <Route path="/awards" element={<AwardsTracker />} />

          {/* Admin — only accessible to admins (guarded in page) */}
          <Route path="/admin" element={<Admin />} />

          {/* Insights merged into /teams */}
          <Route path="/insights" element={<Navigate to="/teams" replace />} />

          {/* Trades (primary) — Pro only */}
          <Route path="/trades" element={<RequirePro featureName="Trade Value Engine"><Trades /></RequirePro>} />
          {/* Legacy Analytics redirect */}
          <Route path="/analytics" element={<Navigate to="/trades" replace />} />

          {/* Bettor CLV Tracker retired */}
          <Route path="/bettor" element={<Navigate to="/" replace />} />

          {/* Compare Players retired */}
          <Route path="/compare" element={<Navigate to="/players" replace />} />
          <Route path="/compare/players" element={<Navigate to="/players" replace />} />

          {/* Trade Explorer — redirect to /trades */}
          <Route path="/trade" element={<Navigate to="/trades" replace />} />

          {/* Search redirects to players */}
          <Route path="/search" element={<Navigate to="/players" replace />} />

          {/* Settings */}
          <Route path="/settings" element={<Settings />} />

          {/* Password reset (from email link) */}
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Email verification (from email link) */}
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* Boxscore */}
          <Route path="/boxscore/:gameId/:date" element={<BoxscorePage />} />

          {/* 404 */}
          <Route path="*" element={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, flexDirection: 'column', gap: 2, p: 3 }}>
              <Typography variant="h1" sx={{ fontSize: { xs: '3rem', sm: '4rem' }, fontWeight: 800, color: 'text.primary' }}>404</Typography>
              <Typography variant="h6" sx={{ color: 'text.secondary', textAlign: 'center' }}>Page not found</Typography>
              <Button variant="outlined" href="/" sx={{ mt: 2 }}>Back to Dashboard</Button>
            </Box>
          } />
        </Routes>
      </motion.div>
    </AnimatePresence>
    </>
  );
}

// Main App Component
function App() {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('theCycle-darkMode');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiStatus, setApiStatus] = useState('checking');

  const theme = createAppTheme(darkMode ? 'dark' : 'light');

  // Initialize app and check API health
  useEffect(() => {
    const initializeApp = async () => {
      try {
        setLoading(true);
        
        // Add a timeout to prevent infinite loading
        const healthPromise = healthCheck();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Health check timeout')), 5000)
        );
        
        const health = await Promise.race([healthPromise, timeoutPromise]);
        setApiStatus(health.status === 'healthy' ? 'connected' : 'error');
      } catch (error) {
        console.error('❌ Failed to connect to API:', error);
        setApiStatus('error');
        // Continue anyway to show the UI
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, []);

  // Save theme preference
  useEffect(() => {
    localStorage.setItem('theCycle-darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Listen for theme changes from Settings page (no full reload needed)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'theCycle-darkMode' && e.newValue !== null) {
        setDarkMode(JSON.parse(e.newValue));
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Desktop sidebar is persistent (≥900px)
  const isDesktop = useMediaQuery('(min-width:900px)');
  const sidebarWidth = 280;

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoadingScreen />
      </ThemeProvider>
    );
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
      <YearProvider>
      <SettingsProvider>
      <FavoritesProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          {/* Skip to content link for accessibility */}
          <Box
            component="a"
            href="#main-content"
            sx={{
              position: 'fixed',
              top: -100,
              left: 8,
              zIndex: 9999,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              px: 2,
              py: 1,
              borderRadius: 1,
              fontWeight: 600,
              textDecoration: 'none',
              '&:focus': { top: 8 },
            }}
          >
            Skip to content
          </Box>

          <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
            {/* Navigation Header */}
            <Navigation
              darkMode={darkMode}
              onToggleDarkMode={toggleDarkMode}
              onToggleSidebar={toggleSidebar}
              apiStatus={apiStatus}
            />

            {/* Sidebar */}
            <Sidebar
              open={isDesktop ? true : sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              variant={isDesktop ? 'permanent' : 'temporary'}
            />

            {/* Main Content Area */}
            <Box
              component="main"
              id="main-content"
              tabIndex={-1}
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                pt: { xs: 7, sm: 8 }, // Account for navigation height
                minHeight: '100vh',
                backgroundColor: 'background.default',
                overflowX: 'hidden', // Prevent horizontal scroll from child overflow
                outline: 'none',
              }}
            >
              {/* Route transition overlay loader */}
              <RouteTransitionLoader />
              <VerifyEmailBanner />
              <AnimatedRoutes />
            </Box>
          </Box>
          
          {/* Floating widgets — available on all pages */}
          <FloatingGlossary />
        </Router>
      </ThemeProvider>
      </FavoritesProvider>
      </SettingsProvider>
      </YearProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
